import React from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { parse } from 'date-fns';
import AutocompleteSelect from '@components/common/AutoCompleteSelect/AutoCompleteSelect';
import TreeSelect from '@components/common/TreeSelect/TreeSelect';
import { TableHeader } from '@components/common/Table/table.types';

type UseTableViewParams<T> = {
  headerDetails: TableHeader<T>[];
  rowKey?: (row: T, rowIndex: number) => React.Key;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSortChange?: (sortKey: string) => void;
  sortKey?: string | null;
  sortDirection?: 'asc' | 'desc';
  minWidth?: number | string;
  loading?: boolean;
  // Optional external state for activeFilterKey to prevent reset on re-renders
  activeFilterKey?: string | null;
  setActiveFilterKey?: React.Dispatch<React.SetStateAction<string | null>>;
};

export const useTableView = <T,>({
  headerDetails,
  rowKey,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  sortKey,
  sortDirection = 'asc',
  minWidth = 1000,
  loading = false,
  activeFilterKey: externalActiveFilterKey,
  setActiveFilterKey: externalSetActiveFilterKey,
}: UseTableViewParams<T>) => {
  const [internalActiveFilterKey, setInternalActiveFilterKey] = React.useState<string | null>(null);
  // Use external state if provided (prevents reset when headerDetails changes), else use internal
  const activeFilterKey = externalActiveFilterKey !== undefined ? externalActiveFilterKey : internalActiveFilterKey;
  const setActiveFilterKey = externalSetActiveFilterKey ?? setInternalActiveFilterKey;
  const [dateFilterKey, setDateFilterKey] = React.useState<string | null>(null);
  const [pendingDateRange, setPendingDateRange] = React.useState<
    [Date | null, Date | null]
  >([null, null]);
  const [numberRangeFilterKey, setNumberRangeFilterKey] = React.useState<string | null>(null);
  const [pendingNumberRange, setPendingNumberRange] = React.useState<
    [string, string]
  >(['', '']);

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
  };

  const getRowKey = (row: T, index: number) => {
    if (rowKey) {
      return rowKey(row, index);
    }
    return (row as { id?: string | number })?.id ?? index;
  };

  const getActionId = (row: T, index: number) => {
    if (rowKey) {
      const key = rowKey(row, index);
      if (typeof key === 'string' || typeof key === 'number') {
        return key;
      }
    }
    return (row as { id?: string | number })?.id;
  };

  const renderCellValue = (
    column: TableHeader<T>,
    row: T,
    rowIndex: number
  ) => {
    const value = column.render
      ? column.render(row, rowIndex)
      : (row as Record<string, unknown>)[column.key];

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const isIntegerValue = Number.isInteger(value);
      return value.toLocaleString('en-US', {
        useGrouping: true,
        minimumFractionDigits: isIntegerValue ? 0 : 0,
        maximumFractionDigits: isIntegerValue ? 0 : 20,
      });
    }

    return value as React.ReactNode;
  };

  const tableMinWidth = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;
  const getColumnSortKey = (column: TableHeader<T>) =>
    column.sortKey ?? column.key;

  const parseDateValue = (value: unknown) => {
    if (typeof value !== 'string' || !value) return null;
    return parse(value, 'yyyy-MM-dd', new Date());
  };

  const parseDateRangeValue = (
    value: TableHeader<T>['filterValue']
  ): [Date | null, Date | null] => {
    if (Array.isArray(value)) {
      return [parseDateValue(value[0]), parseDateValue(value[1])];
    }
    if (typeof value === 'string' && value) {
      return [parseDateValue(value), null];
    }
    return [null, null];
  };

  const getEmptyFilterValue = (column: TableHeader<T>) => {
    if (
      column.filterMultiple ||
      column.type === 'tree' ||
      ((column.type === 'dropdown' || column.type === 'autocomplete') &&
        column.filterMultiple !== false)
    ) {
      return [];
    }
    if (column.type === 'number') {
      return null;
    }
    return '';
  };

  const isFilterActive = (column: TableHeader<T>) => {
    const value = column.filterValue;
    if (Array.isArray(value)) {
      return value.some((item) => item !== undefined && item !== null && item !== '');
    }
    // Handle number filter object
    if (typeof value === 'object' && value !== null && 'operator' in value) {
      return value.value !== undefined && value.value !== null;
    }
    return value !== undefined && value !== null && value !== '';
  };

  const normalizeAutocompleteValue = (
    val: TableHeader<T>['filterValue']
  ): string | string[] => {
    if (Array.isArray(val)) {
      return val.map((item) => String(item));
    }
    if (val === null || val === undefined) {
      return '';
    }
    return String(val);
  };

  const normalizeTextFilterValue = (input: string) => {
    const trimmed = input.trim();
    if (!trimmed) {
      return '';
    }
    const isNumeric = /^[+-]?\d+(\.\d+)?$/.test(trimmed);
    if (isNumeric && !trimmed.includes('.')) {
      return Number(trimmed);
    }
    return input;
  };

  const renderFilterControl = (column: TableHeader<T>) => {
    const value = column.filterValue ?? getEmptyFilterValue(column);
    const labelText = typeof column.label === 'string' ? column.label : 'value';
    const placeholder =
      column.filterPlaceholder || `Search ${labelText}...`;
    if (column.type === 'select') {
      return (
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <Select
            size="small"
            value={value as string | number}
            onChange={(e) => {
              column.onFilterChange?.(e.target.value as string | number);
            }}
            onClose={() => setActiveFilterKey(null)}
            autoFocus
            displayEmpty
            sx={{ minWidth: 120, flex: 1 }}
            disabled={loading}
          >
            {column.filterAllLabel && (
              <MenuItem value="">{column.filterAllLabel}</MenuItem>
            )}
            {column.dropdownOptions?.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      );
    }

    if (column.type === 'dropdown' || column.type === 'autocomplete') {
      const options =
        column.dropdownOptions?.map((option) => ({
          ...option,
          value: String(option.value),
        })) ?? [];
      const normalizedValue = normalizeAutocompleteValue(value);
      return (
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AutocompleteSelect
              options={options}
              value={normalizedValue}
              multiple={column.filterMultiple}
              placeholder={placeholder}
              loading={column.filterLoading}
              searchValue={column.filterInputValue}
              onSearchChange={column.onFilterInputChange}
              searchable={Boolean(column.onFilterInputChange)}
              onChange={(val) =>
                column.onFilterChange?.(
                  val as Array<string>
                )
              }
              onBlur={() => setActiveFilterKey(null)}
              onScrollEnd={column.onFilterScrollEnd}
              disabled={loading}
            />
          </Box>
        </Box>
      );
    }

    if (column.type === 'tree') {
      return (
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TreeSelect
              items={column.treeOptions ?? []}
              value={value as string[]}
              onChange={(val) => column.onFilterChange?.(val as string[])}
              onBlur={() => setActiveFilterKey(null)}
              label={labelText}
              placeholder={column.filterPlaceholder || 'Select'}
              multiple={column.filterMultiple ?? true}
              searchable
              searchValue={column.filterSearchValue || ''}
              onSearchChange={column.onFilterSearchChange}
              isLoading={column.filterLoading}
              menuWidth={typeof column.menuWidth === 'number' ? column.menuWidth : 320}
              minMenuWidth={
                typeof column.minMenuWidth === 'number' ? column.minMenuWidth : 260
              }
              maxMenuWidth={
                typeof column.maxMenuWidth === 'number' ? column.maxMenuWidth : 360
              }
              disabled={loading}
            />
          </Box>
        </Box>
      );
    }


    if (column.type === 'number') {
      // For number filters, we expect the value to be an object like { operator: 'gte', value: 100 }
      // or a string for simple exact matches
      const numberValue = typeof value === 'object' && value !== null
        ? (value as { operator?: string; value?: number })
        : { operator: 'exact', value: typeof value === 'number' ? value : undefined };

      const operators = [
        { value: 'exact', label: 'Equals' },
        { value: 'gte', label: 'Greater than or equal' },
        { value: 'lte', label: 'Less than or equal' },
        { value: 'gt', label: 'Greater than' },
        { value: 'lt', label: 'Less than' },
      ];

      return (
        <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
          <Select
            size="small"
            value={numberValue.operator || 'exact'}
            onChange={(e) => {
              const newOperator = e.target.value;
              column.onFilterChange?.({
                operator: newOperator,
                value: numberValue.value || 0
              });
            }}
            sx={{ minWidth: 80, flex: 0 }}
            disabled={loading}
          >
            {operators.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            type="number"
            placeholder="Value"
            value={numberValue.value || ''}
            onChange={(e) => {
              const newValue = e.target.value ? parseFloat(e.target.value) : undefined;
              column.onFilterChange?.({
                operator: numberValue.operator || 'exact',
                value: newValue
              });
            }}
            onBlur={() => setActiveFilterKey(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setActiveFilterKey(null);
              }
            }}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                minWidth: 0, // Allow input to shrink
                '& .MuiInputBase-input': {
                  minWidth: 0, // Allow input field to shrink
                  paddingRight: isFilterActive(column) ? '8px' : '12px', // Adjust padding when clear button is present
                },
              },
              '& .MuiInputAdornment-root': {
                marginLeft: '4px', // Reduce margin to save space
              },
            }}
            disabled={loading}
            InputProps={{
              endAdornment: isFilterActive(column) && (
                <InputAdornment position="end">
                  <IconButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => column.onFilterChange?.(null)}
                    size="small"
                    disabled={loading}
                    sx={{
                      padding: '2px', // Reduce padding to make button smaller
                      minWidth: 'auto', // Remove minimum width
                      '& .MuiSvgIcon-root': {
                        fontSize: '16px', // Make icon smaller
                      },
                    }}
                  >
                    <ClearIcon fontSize="medium" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      );
    }

    return (
      <TextField
        size="small"
        autoFocus
        placeholder={placeholder}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          column.onFilterChange?.(e.target.value);
        }}
        onBlur={() => setActiveFilterKey(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setActiveFilterKey(null);
          }
        }}
        sx={{
          '& .MuiInputBase-root': {
            minWidth: 0, // Allow input to shrink
            '& .MuiInputBase-input': {
              minWidth: 0, // Allow input field to shrink
              paddingRight: isFilterActive(column) ? '8px' : '12px', // Adjust padding when clear button is present
            },
          },
          '& .MuiInputAdornment-root': {
            marginLeft: '4px', // Reduce margin to save space
          },
        }}
        InputProps={{
          endAdornment: isFilterActive(column) && (
            <InputAdornment position="end">
              <IconButton
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  column.onFilterChange?.(getEmptyFilterValue(column) as string)
                }
                size="small"
                disabled={loading}
                sx={{
                  padding: '2px', // Reduce padding to make button smaller
                  minWidth: 'auto', // Remove minimum width
                  '& .MuiSvgIcon-root': {
                    fontSize: '16px', // Make icon smaller
                  },
                }}
              >
                <ClearIcon fontSize="medium" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={loading}
      />
    );
  };

  const makeHeader = (column: TableHeader<T>) => {
    const isSortable = Boolean(
      !loading && onSortChange && column.sortable !== false
    );
    const columnSortKey = getColumnSortKey(column);
    const filterable = Boolean(!loading && column.type && column.onFilterChange);
    const isDropdown =
      column.type === 'dropdown' ||
      column.type === 'tree' ||
      column.type === 'autocomplete' ||
      column.type === 'select';
    const isDatePicker = column.type === 'datePicker';
    const isNumberFilter = column.type === 'number';
    const isNumberRange = column.type === 'numberRange';

    if (filterable && activeFilterKey === column.key && !isDatePicker && !isNumberRange) {
      return renderFilterControl(column);
    }

    const labelContent = isSortable ? (
      <Tooltip title={column.label} arrow>
        <TableSortLabel
          active={sortKey === columnSortKey}
          direction={sortKey === columnSortKey ? sortDirection : 'asc'}
          onClick={() => {
            onSortChange?.(columnSortKey);
          }}
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {column.label}
        </TableSortLabel>
      </Tooltip>
    ) : (
      <Tooltip title={column.label} arrow>
        <Box
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {column.label}
        </Box>
      </Tooltip>
    );

    if (!filterable) {
      return (
        <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
          {labelContent}
        </Box>
      );
    }

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ minWidth: 0 }}>
        {labelContent}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (isDatePicker) {
              setDateFilterKey(column.key);
              setPendingDateRange(parseDateRangeValue(column.filterValue));
            } else if (isNumberRange) {
              setNumberRangeFilterKey(column.key);
              const currentValue = Array.isArray(column.filterValue) ? column.filterValue : ['', ''];
              setPendingNumberRange([String(currentValue[0] || ''), String(currentValue[1] || '')]);
            } else {
              setActiveFilterKey(column.key);
            }
          }}
          color={isFilterActive(column) ? 'primary' : 'default'}
          disabled={loading}
          sx={{ flexShrink: 0 }} // Prevent icon from shrinking
        >
          {isDatePicker ? (
            <CalendarTodayIcon fontSize="small" />
          ) : isDropdown || isNumberFilter || isNumberRange ? (
            <FilterListIcon fontSize="small" />
          ) : (
            <SearchIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    );
  };

  const activeDateColumn = dateFilterKey
    ? headerDetails.find((col) => col.key === dateFilterKey)
    : null;

  const activeNumberRangeColumn = numberRangeFilterKey
    ? headerDetails.find((col) => col.key === numberRangeFilterKey)
    : null;

  return {
    activeFilterKey,
    dateFilterKey,
    pendingDateRange,
    numberRangeFilterKey,
    pendingNumberRange,
    setActiveFilterKey,
    setDateFilterKey,
    setPendingDateRange,
    setNumberRangeFilterKey,
    setPendingNumberRange,
    tableMinWidth,
    renderCellValue,
    getRowKey,
    getActionId,
    makeHeader,
    activeDateColumn,
    activeNumberRangeColumn,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};
